import React, { createContext, useContext, useState, ReactNode } from 'react';
import { NotificationToast } from '../types';

interface NotificationContextType {
  notifications: NotificationToast[];
  addNotification: (notification: Omit<NotificationToast, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationToast[]>([]);

  const addNotification = (notification: Omit<NotificationToast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification: NotificationToast = {
      ...notification,
      id,
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification after duration (default 5 seconds).
    // Errors stay until dismissed so a failed save can't be missed,
    // unless the caller passes an explicit duration.
    if (notification.type === 'error' && !notification.duration) {
      return;
    }
    const duration = notification.duration || 5000;
    setTimeout(() => {
      removeNotification(id);
    }, duration);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};